import { useState } from 'react';
import { Box, Select } from '@chakra-ui/react';

import { KycReviewDocumentStatus } from '__generated__/GQL';
import asBentoPage from 'common/bento/hoc/asBentoPage';
import Button from 'components/_core/Button';
import Text from 'components/_core/Text';
import {
  getKycDocumentsForDocTypes,
  getRemainingDocTypesToUpload,
} from 'features/Bento/libs/kycDocuments';
import OBNavigation from 'features/Onboarding/components/OBNavigation/OBNavigation';

import Header from '../../../../components/Header';
import UploadContent from '../../components/UploadContent/UploadContent';
import { AddressModuleData, DocumentFile } from '../../machine/model';
import { getInitialIdType } from './libs';
import { HOST_ID_DOCUMENT_TYPES, IdType } from './libs/constants';
import locales from './locales';

interface HostIdProps {
  moduleData: AddressModuleData;
  isLoading: boolean;
  isUploaderLoading: boolean;
  onSuccess: (doc: DocumentFile) => void;
  onContinue: () => void;
  onDeleteFileClick: (documentId: string) => void;
}

/**
  A page asking the user to upload the ID of their host.
 */
const HostId = asBentoPage<HostIdProps>(
  ({
    isLoading,
    isUploaderLoading,
    Layout,
    moduleData,
    onContinue,
    onDeleteFileClick,
    onPrev,
    onSuccess,
  }) => {
    const kycDocuments = moduleData.viewer.companyUser.kycDocuments.filter(
      ({ status }) => status !== KycReviewDocumentStatus.Refused,
    );

    const [idType, setIdType] = useState<IdType | undefined>(() =>
      getInitialIdType(kycDocuments),
    );

    const renderUploader = () => {
      if (!idType) {
        return null;
      }

      const remainingDocTypesToUpload = getRemainingDocTypesToUpload(
        kycDocuments,
        HOST_ID_DOCUMENT_TYPES[idType],
      );
      const kycDocumentForIdType = getKycDocumentsForDocTypes(
        kycDocuments,
        HOST_ID_DOCUMENT_TYPES[idType],
      );

      return (
        <>
          <Text
            fontWeight="weight-500"
            marginBottom="space-16"
            marginTop="space-40"
            size="lg"
            variant="primary"
          >
            2. {locales.upload[idType]}
          </Text>

          <UploadContent
            areFileActionsDisabled={isLoading}
            documentTypeToUpload={remainingDocTypesToUpload[0] ?? undefined}
            onDeleteFileClick={onDeleteFileClick}
            onSuccessfulUpload={onSuccess}
            showUploaderLoading={isUploaderLoading}
            uploadedDocuments={kycDocumentForIdType}
          />
        </>
      );
    };

    const renderCardFooter = () => {
      if (!idType) {
        return null;
      }

      const remainingDocTypesToUpload = getRemainingDocTypesToUpload(
        kycDocuments,
        HOST_ID_DOCUMENT_TYPES[idType],
      );

      return (
        remainingDocTypesToUpload.length === 0 && (
          <Button
            isDisabled={isLoading}
            isExpanded
            marginTop="space-48"
            onClick={onContinue}
          >
            {locales.next}
          </Button>
        )
      );
    };

    return (
      <Layout nav={<OBNavigation onPrev={onPrev} />}>
        <Box>
          <Header description={locales.description} title={locales.title} />

          <Text
            fontWeight="weight-500"
            marginBottom="space-16"
            size="lg"
            variant="primary"
          >
            1. {locales.selectDocumentType}
          </Text>

          <Text marginBottom="space-4">{locales.selectLabel}</Text>

          <Select
            disabled={isLoading}
            onChange={(e) => setIdType(e.target.value as IdType)}
            placeholder={locales.selectLabel}
            value={idType}
          >
            <option value={IdType.Id}>{locales.idCard}</option>

            <option value={IdType.ResidencePermit}>
              {locales.residencePermit}
            </option>

            <option value={IdType.Passport}>{locales.passport}</option>
          </Select>

          {renderUploader()}

          {renderCardFooter()}
        </Box>
      </Layout>
    );
  },
);

export default HostId;
